import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ListTodo, Circle, CheckCircle2, Pencil, Trash2, Repeat, Clock, Loader2, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import DatePicker from "../components/DatePicker";
import { useLanguage } from "../context/LanguageContext";
import { REMINDER_COLOR } from "../lib/reminders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECURRENCES = ["none", "weekly", "monthly", "yearly"];

const EMPTY_FORM = { title: "", notes: "", due_date: "", due_time: "", recurrence: "none" };

/** "2026-10-17" for today, in local terms rather than through UTC. */
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Past its moment: an earlier day, or today at a time already gone. */
function isOverdue(r) {
  if (!r.due_date) return false;
  const today = todayIso();
  if (r.due_date < today) return true;
  return r.due_date === today && Boolean(r.due_time) && r.due_time < nowHHMM();
}

/** A due date as the reader's language writes it, with the year only when it is not this one. */
function formatDate(iso, locale) {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const options = { weekday: "short", day: "numeric", month: "short" };
  if (y !== new Date().getFullYear()) options.year = "numeric";
  return date.toLocaleDateString(locale, options);
}

/** What the page sends: empty strings become nulls, so a cleared field is cleared. */
function toPayload(form) {
  const hasDate = Boolean(form.due_date);
  return {
    title: form.title.trim(),
    notes: form.notes.trim() || null,
    due_date: form.due_date || null,
    due_time: hasDate ? form.due_time || null : null,
    recurrence: hasDate ? form.recurrence : "none",
  };
}

/**
 * The fields of a reminder, shared by the new-reminder card and the edit dialog
 * so the two can never ask for different things.
 *
 * Time and repeat are disabled until there is a date, because the server
 * refuses both without one; saying so beside them is better than a refusal
 * after the click.
 */
function ReminderFields({ form, setForm, t, idPrefix }) {
  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  const hasDate = Boolean(form.due_date);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-title`}>{t("reminders.titleLabel")}</Label>
        <Input
          id={`${idPrefix}-title`}
          value={form.title}
          maxLength={200}
          onChange={(e) => set("title")(e.target.value)}
          placeholder={t("reminders.titlePlaceholder")}
          data-testid={`${idPrefix}-title`}
          className="border-slate-200"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-notes`}>{t("reminders.notes")}</Label>
        <Textarea
          id={`${idPrefix}-notes`}
          value={form.notes}
          maxLength={2000}
          rows={2}
          onChange={(e) => set("notes")(e.target.value)}
          placeholder={t("reminders.notesPlaceholder")}
          className="border-slate-200 resize-none"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>{t("reminders.date")}</Label>
          <DatePicker
            testId={`${idPrefix}-date`}
            value={form.due_date}
            onChange={(v) => set("due_date")(v || "")}
            placeholder={t("reminders.datePlaceholder")}
          />
          {hasDate && (
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, due_date: "", due_time: "", recurrence: "none" }))}
              className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            >
              {t("reminders.clearDate")}
            </button>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-time`}>{t("reminders.time")}</Label>
          <Input
            id={`${idPrefix}-time`}
            type="time"
            value={form.due_time}
            disabled={!hasDate}
            onChange={(e) => set("due_time")(e.target.value)}
            className="border-slate-200"
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("reminders.repeat")}</Label>
          <Select value={form.recurrence} onValueChange={set("recurrence")} disabled={!hasDate}>
            <SelectTrigger className="border-slate-200" data-testid={`${idPrefix}-repeat`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECURRENCES.map((r) => (
                <SelectItem key={r} value={r}>{t(`reminders.repeat.${r}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!hasDate && <p className="text-xs text-slate-400">{t("reminders.needsDate")}</p>}
    </div>
  );
}

/**
 * Personal reminders: one account's own list, with optional due dates that
 * show on the home calendar and bring a notice to the bell when they arrive.
 */
export default function Reminders() {
  const { t, language } = useLanguage();
  const locale = language === "it" ? "it-IT" : "en-US";

  const [open, setOpen] = useState([]);
  const [done, setDone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reminders`, { withCredentials: true });
      setOpen(data.open || []);
      setDone(data.done || []);
    } catch {
      toast.error(t("reminders.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    // Opening the page is reading the bell's reminder notices, the same way
    // opening a project reads its updates.
    axios.post(`${API}/notifications/read/reminders`, {}, { withCredentials: true }).catch(() => {});
  }, [load]);

  const fail = (err) => toast.error(err.response?.data?.detail || t("common.actionFailed"));

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/reminders`, toPayload(form), { withCredentials: true });
      setForm(EMPTY_FORM);
      toast.success(t("reminders.created"));
      load();
    } catch (err) {
      fail(err);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (r) => {
    setEditing(r);
    setEditForm({
      title: r.title,
      notes: r.notes || "",
      due_date: r.due_date || "",
      due_time: r.due_time || "",
      recurrence: r.recurrence || "none",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing || !editForm.title.trim()) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/reminders/${editing.id}`, toPayload(editForm), { withCredentials: true });
      setEditing(null);
      toast.success(t("reminders.saved"));
      load();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const complete = async (r) => {
    try {
      const { data } = await axios.post(`${API}/reminders/${r.id}/done`, {}, { withCredentials: true });
      // A recurring reminder does not close; it moves on, and says where to.
      if (!data.done && data.due_date) {
        toast.success(t("reminders.nextOn", { date: formatDate(data.due_date, locale) }));
      } else {
        toast.success(t("reminders.completed"));
      }
      load();
    } catch (err) {
      fail(err);
    }
  };

  const reopen = async (r) => {
    try {
      await axios.post(`${API}/reminders/${r.id}/reopen`, {}, { withCredentials: true });
      load();
    } catch (err) {
      fail(err);
    }
  };

  const remove = async (r) => {
    try {
      await axios.delete(`${API}/reminders/${r.id}`, { withCredentials: true });
      toast.success(t("reminders.deleted"));
      load();
    } catch (err) {
      fail(err);
    }
  };

  const row = (r) => {
    const overdue = !r.done && isOverdue(r);
    const today = !r.done && !overdue && r.due_date === todayIso();
    return (
      <li
        key={r.id}
        data-testid={`reminder-${r.id}`}
        className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0"
      >
        <button
          type="button"
          onClick={() => (r.done ? reopen(r) : complete(r))}
          aria-label={r.done ? t("reminders.reopen") : t("reminders.markDone")}
          title={r.done ? t("reminders.reopen") : t("reminders.markDone")}
          className="mt-0.5 shrink-0 text-slate-400 hover:text-slate-700 transition-colors"
          style={r.done ? { color: REMINDER_COLOR } : undefined}
        >
          {r.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
        </button>

        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold break-words ${r.done ? "text-slate-400 line-through" : "text-slate-900"}`}>
            {r.title}
          </p>
          {r.notes && (
            <p className={`text-sm mt-0.5 whitespace-pre-line break-words ${r.done ? "text-slate-400" : "text-slate-600"}`}>
              {r.notes}
            </p>
          )}
          {r.due_date && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs">
              <span
                className={`inline-flex items-center gap-1 font-medium ${
                  overdue ? "text-red-600" : today ? "" : "text-slate-500"
                }`}
                style={today ? { color: REMINDER_COLOR } : undefined}
              >
                <Clock className="w-3 h-3" />
                {formatDate(r.due_date, locale)}
                {r.due_time ? ` ${r.due_time}` : ""}
              </span>
              {overdue && (
                <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-semibold">{t("reminders.overdue")}</span>
              )}
              {today && (
                <span className="px-1.5 py-0.5 rounded font-semibold text-white" style={{ backgroundColor: REMINDER_COLOR }}>
                  {t("reminders.today")}
                </span>
              )}
              {r.recurrence && r.recurrence !== "none" && (
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Repeat className="w-3 h-3" />
                  {t(`reminders.repeat.${r.recurrence}`)}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {!r.done && (
            <button
              type="button"
              onClick={() => startEdit(r)}
              aria-label={t("reminders.edit")}
              title={t("reminders.edit")}
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                aria-label={t("reminders.delete")}
                title={t("reminders.delete")}
                className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("reminders.deleteTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("reminders.deleteText")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-slate-200">{t("reminders.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => remove(r)} className="bg-red-600 hover:bg-red-500 text-white">
                  {t("reminders.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </li>
    );
  };

  return (
    <div className="min-h-screen">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t("reminders.back")}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: REMINDER_COLOR }}>
            <ListTodo className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("reminders.title")}</h1>
        </div>

        <form onSubmit={create} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm mb-6">
          <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">{t("reminders.new")}</h2>
          <ReminderFields form={form} setForm={setForm} t={t} idPrefix="reminder-new" />
          <div className="flex justify-end mt-4">
            <Button
              type="submit"
              disabled={creating || !form.title.trim()}
              data-testid="reminder-add"
              className="bg-indigo-700 hover:bg-indigo-600 text-white"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reminders.add")}
            </Button>
          </div>
        </form>

        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden">
          <h2 className="px-4 pt-4 pb-2 text-xs uppercase tracking-widest font-bold text-slate-400">
            {t("reminders.open")} {open.length > 0 && <span className="text-slate-300">· {open.length}</span>}
          </h2>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
          ) : open.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">{t("reminders.emptyOpen")}</p>
          ) : (
            <ul>{open.map(row)}</ul>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-4 text-xs uppercase tracking-widest font-bold text-slate-400 hover:text-slate-600"
          >
            <span>
              {t("reminders.done")} {done.length > 0 && <span className="text-slate-300">· {done.length}</span>}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showDone ? "rotate-180" : ""}`} />
          </button>
          {showDone && (
            done.length === 0
              ? <p className="text-sm text-slate-400 text-center pb-8">{t("reminders.emptyDone")}</p>
              : <ul className="border-t border-slate-100">{done.map(row)}</ul>
          )}
        </section>
      </main>

      <Dialog open={Boolean(editing)} onOpenChange={(v) => { if (!v && !saving) setEditing(null); }}>
        <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t("reminders.edit")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit}>
            <ReminderFields form={editForm} setForm={setEditForm} t={t} idPrefix="reminder-edit" />
            <div className="flex flex-wrap justify-end gap-3 pt-5">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={saving} className="border-slate-200">
                {t("reminders.cancel")}
              </Button>
              <Button type="submit" disabled={saving || !editForm.title.trim()} className="bg-indigo-700 hover:bg-indigo-600 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reminders.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
